//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { Action } from 'redux';

import { addPostToLibraryByUrl } from '@apis/gatewayMediaApi';
import {
  INSTAGRAM_POST_UPLOADER_ACTIONS,
  INSTAGRAM_POST_UPLOADER_CONTEXT,
} from '@instagram-post-uploader/constants/instagramPostUploaderConstants';
import { createAction } from '@utils/actions';
import { ReduxDispatch } from 'app/curalate.store';

interface ISetInstagramPostUploaderContext extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.SET_CONTEXT;
  pageContext: INSTAGRAM_POST_UPLOADER_CONTEXT;
}

const openInstagramPostUploaderModal = () =>
  createAction({
    type: INSTAGRAM_POST_UPLOADER_ACTIONS.OPEN_MODAL,
  });

interface ICloseInstagramPostUploaderModal extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.CLOSE_MODAL;
}

interface IUpdatePostUrlValue extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.UPDATE_POST_URL_VALUE;
  urlToUpload: string;
}

interface IValidatePostUrlValue extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.VALIDATE_POST_URL_VALUE;
  isValid: boolean;
}

interface IAddPost extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.ADD_POST;
}

interface IUploadPostRequest extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.REQUEST_POST_UPLOAD;
}

interface IUploadPostReceiveSucceeded extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.RECEIVE_POST_UPLOAD_SUCCEEDED;
}

interface IUploadPostReceiveFailed extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.RECEIVE_POST_UPLOAD_FAILED;
}

interface IUpdateErrorMessage extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.UPDATE_ERROR_MESSAGE;
  message: string;
}

interface IResetErrorMessage extends Action {
  type: INSTAGRAM_POST_UPLOADER_ACTIONS.RESET_ERROR_MESSAGE;
}

export type InstagramPostUploaderActionType =
  | ISetInstagramPostUploaderContext
  | ReturnType<typeof openInstagramPostUploaderModal>
  | ICloseInstagramPostUploaderModal
  | IUpdatePostUrlValue
  | IValidatePostUrlValue
  | IAddPost
  | IUploadPostRequest
  | IUploadPostReceiveSucceeded
  | IUploadPostReceiveFailed
  | IUpdateErrorMessage
  | IResetErrorMessage;

export const instagramPostUploaderActions = {
  openInstagramPostUploaderModal,

  setInstagramPostUploaderContext(
    pageContext: INSTAGRAM_POST_UPLOADER_CONTEXT,
  ): ISetInstagramPostUploaderContext {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.SET_CONTEXT,
      pageContext,
    };
  },

  closeInstagramPostUploaderModal(): ICloseInstagramPostUploaderModal {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.CLOSE_MODAL,
    };
  },

  updatePostUrlValue(urlToUpload: string): IUpdatePostUrlValue {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.UPDATE_POST_URL_VALUE,
      urlToUpload,
    };
  },

  validatePostUrlValue(isValid: boolean): IValidatePostUrlValue {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.VALIDATE_POST_URL_VALUE,
      isValid,
    };
  },

  addPost: (url: string) => async (dispatch: ReduxDispatch) => {
    dispatch(instagramPostUploaderActions.requestPostUpload());

    try {
      // Add the label so we can differentiate between these library import items and curalate save button items
      // since they both generate instagram network cuids
      const cuid = await addPostToLibraryByUrl({
        url,
        labels: ['library-import']
      });
      dispatch(instagramPostUploaderActions.receivePostUploadSucceded());
      return cuid;
    } catch (err) {
      dispatch(instagramPostUploaderActions.receivePostUploadFailed());
      return err;
    }
  },

  requestPostUpload(): IUploadPostRequest {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.REQUEST_POST_UPLOAD,
    };
  },

  receivePostUploadSucceded(): IUploadPostReceiveSucceeded {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.RECEIVE_POST_UPLOAD_SUCCEEDED,
    };
  },

  receivePostUploadFailed(): IUploadPostReceiveFailed {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.RECEIVE_POST_UPLOAD_FAILED,
    };
  },

  updateErrorMessage(message: string): IUpdateErrorMessage {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.UPDATE_ERROR_MESSAGE,
      message,
    };
  },

  resetErrorMessage(): IResetErrorMessage {
    return {
      type: INSTAGRAM_POST_UPLOADER_ACTIONS.RESET_ERROR_MESSAGE,
    };
  },
};
